import classnames from 'classnames'
import { useState, FC, useEffect, useRef } from 'react'

import { Icon, Menu, Input, Typography } from 'components/ui'
import { Modal } from '~shared'
import { useScreenClass, useOnClickOutside } from '~hooks'

import styles from './Dropdown.module.scss'

type Option = {
  value: string | number
  label: string
}

type DropdownProps = {
  options: Array<Option>
  withFilter?: boolean
  onSelect: (value: string) => void
  setError?: () => void
  value?: string | number
  onFilterInputChange?: (v: string) => void
  withOuterFilter?: boolean
  placeholder?: string
  withMobileModal?: boolean
}

type DropdownBodyProps = {
  onClick: (value: string) => void
  options: Array<Option>
}

const Body: FC<DropdownBodyProps> = ({ onClick, options }) => (
  <div className={classnames(styles.body)} aria-hidden>
    <div className={styles['body-inner']}>
      {options.length === 0 ? (
        <div className={styles['not-found']}>
          <Typography.Text>Not found</Typography.Text>
        </div>
      ) : (
        <Menu items={options} onClick={onClick} />
      )}
    </div>
  </div>
)

const Dropdown: FC<DropdownProps> = ({
  options,
  onSelect,
  withFilter = false,
  setError,
  value = '',
  onFilterInputChange,
  withOuterFilter = false,
  placeholder,
  withMobileModal = true,
}) => {
  const [open, setOpen] = useState(false)
  const [filterInput, setFilterInput] = useState('')

  const ref = useRef(null)

  const onToggle = () => setOpen((prev) => !prev)

  const { isMobile } = useScreenClass()

  const withMobileVersion = isMobile && withMobileModal

  const getLabel = (v) => options.find((el) => el.value === v)?.label

  const onClick = (v: string) => {
    setFilterInput(getLabel(v))
    onSelect(v)
    onToggle()
  }

  const onFocus = () => {
    if (!open) {
      setError?.()
      setFilterInput('')
    }
    onToggle()
  }

  const onChange = (e) => {
    const { value: v } = e.target

    setFilterInput(v)
    onFilterInputChange?.(v)

    if (!open) {
      setOpen(true)
    }
  }

  useEffect(() => {
    const label = getLabel(value)

    if (label) {
      setFilterInput(label)
    }
  }, [value])

  useOnClickOutside(ref, () => setOpen(false))

  return (
    <div
      className={classnames(styles.dropdown, {
        [styles['dropdown-opened']]: open,
        [styles['not-selected']]: !value,
      })}
      ref={ref}
    >
      {(!withFilter || withMobileVersion) && (
        <button type="button" onClick={onToggle}>
          <span>{filterInput || options[0]?.label}</span>
          <Icon name="arrow" width="0.7em" height="0.7em" />
        </button>
      )}

      {withMobileVersion && (
        <Modal modalClassName={styles.modal} isOpen={open} onClose={() => setOpen(false)}>
          <Body options={options} onClick={onClick} />
        </Modal>
      )}

      {withFilter && !withMobileVersion && (
        <Input
          value={filterInput}
          withError={false}
          placeholder={placeholder}
          onFocus={onFocus}
          icon="arrow"
          onChange={onChange}
        />
      )}

      {!withMobileVersion && open && (
        <Body
          options={
            withFilter && filterInput && !withOuterFilter
              ? options.filter((v) =>
                  v.label.includes(filterInput[0]?.toUpperCase() + filterInput?.slice(1))
                )
              : options
          }
          onClick={onClick}
        />
      )}
    </div>
  )
}

export default Dropdown
